import type { TFunction } from 'i18next';

/**
 * Converts a time string to seconds.
 * @param value - The time value to convert, e.g. "01:30:00" or "90:00"
 * @param format - The format of the time string, either 'hh:mm:ss' or 'mm:ss'
 * @param t - The translation function
 * @returns An object containing the converted value in seconds and any error message
 */
export function convertToSeconds(value: string, format?: 'hh:mm:ss' | 'mm:ss', t?: TFunction) {
  const isNegative = value.startsWith('-');
  const absValue = isNegative ? value.slice(1) : value;
  const valueToCheck = format === 'mm:ss' ? `00:${absValue}` : absValue;
  const timeParts = valueToCheck.split(':').map((part) => parseInt(part, 10));

  const regEx = /^-?(\d{1,2}:\d{2}:\d{2}|\d{1,3}:\d{2})$/;

  if (!regEx.test(value) || timeParts.length !== 3) {
    return {
      value: 0,
      error:
        format === 'mm:ss'
          ? t
            ? t('manager.eventSummary.cuePoints.validation.wrongFormatMMSS')
            : 'mm:ss'
          : t
            ? t('manager.eventSummary.cuePoints.validation.wrongFormatHHMMSS')
            : 'hh:mm:ss',
    };
  }

  const [hours, minutes, seconds] = timeParts;
  const offsetInSeconds = hours * 3600 + minutes * 60 + seconds;

  return { value: offsetInSeconds * (isNegative ? -1 : 1), error: undefined };
}

/**
 * Converts a time offset in milliseconds to a formatted string.
 * @param offsetInMs - The offset in milliseconds to convert to a string.
 * @param withHour - Whether to include hours in the output.
 * @returns The formatted time string.
 */
export function offsetInMsToString(offsetInMs: number, withHour?: boolean) {
  /** Convert time to a positive number if its negative and add the minus at the end again */
  const factor = offsetInMs < 0 ? -1 : 1;

  const totalSeconds = Math.floor((offsetInMs * factor) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${factor === -1 ? '-' : ''}${withHour ? `${hours.toString().padStart(2, '0')}:` : ''}${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
}
